import React from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, X, AlertCircle } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';

const APPROVER_ROLES = [
  { value: 'SUPERVISOR', labelKey: 'approvalComp.roles.supervisor', icon: '👔', descKey: 'approvalComp.roles.supervisorDesc' },
  { value: 'MANAGER',    labelKey: 'approvalComp.roles.manager',    icon: '👨‍💼', descKey: 'approvalComp.roles.managerDesc' },
  { value: 'HR',         labelKey: 'approvalComp.roles.hr',         icon: '👥', descKey: 'approvalComp.roles.hrDesc' },
  { value: 'ADMIN',      labelKey: 'approvalComp.roles.admin',      icon: '⭐', descKey: 'approvalComp.roles.adminDesc' },
  { value: 'CUSTOM',     labelKey: 'approvalComp.roles.custom',     icon: '👤', descKey: 'approvalComp.roles.customDesc' },
];

const getRoleInfo = (roleValue) =>
  APPROVER_ROLES.find((r) => r.value === roleValue) || APPROVER_ROLES[0];

const WorkflowFormDialog = ({
  isDialogOpen,
  handleCloseDialog,
  editingWorkflow,
  formData,
  handleInputChange,
  stepFormData,
  handleStepInputChange,
  editingStepIndex,
  handleAddStep,
  handleUpdateStep,
  handleDeleteStep,
  handleMoveStep,
  handleSubmit,
  handleCancelStepEdit,
}) => {
  const { t } = useTranslation();
  return (
  <Dialog open={isDialogOpen} onOpenChange={handleCloseDialog}>
    <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>
          {editingWorkflow ? t('approvalComp.editTitle') : t('approvalComp.newTitle')}
        </DialogTitle>
        <DialogDescription>
          {t('approvalComp.formDesc')}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-6">
        {/* Informations générales */}
        <div className="space-y-4">
          <h3 className="font-semibold">{t('approvalComp.generalInfo')}</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t('approvalComp.nameLabel')}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder={t('approvalComp.namePlaceholder')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="code">{t('approvalComp.codeLabel')}</Label>
              <Input
                id="code"
                value={formData.code}
                onChange={(e) => handleInputChange('code', e.target.value.toUpperCase())}
                placeholder="STD"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">{t('approvalComp.descLabel')}</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder={t('approvalComp.descPlaceholder')}
            />
          </div>
        </div>

        {/* Configuration */}
        <div className="space-y-4">
          <h3 className="font-semibold">{t('approvalComp.configTitle')}</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => handleInputChange('is_active', checked)}
              />
              <Label htmlFor="is_active">{t('approvalComp.workflowActive')}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_default"
                checked={formData.is_default}
                onCheckedChange={(checked) => handleInputChange('is_default', checked)}
              />
              <Label htmlFor="is_default">{t('approvalComp.defaultCircuit')}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="require_all_steps"
                checked={formData.require_all_steps}
                onCheckedChange={(checked) => handleInputChange('require_all_steps', checked)}
              />
              <Label htmlFor="require_all_steps">{t('approvalComp.allStepsMandatory')}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="allow_parallel_approval"
                checked={formData.allow_parallel_approval}
                onCheckedChange={(checked) => handleInputChange('allow_parallel_approval', checked)}
              />
              <Label htmlFor="allow_parallel_approval">{t('approvalComp.parallelApprovals')}</Label>
            </div>
          </div>
        </div>

        {/* Étapes */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">{t('approvalComp.stepsTitle', { count: formData.steps.length })}</h3>
          </div>

          {formData.steps.length > 0 && (
            <div className="space-y-2 border rounded-lg p-4">
              {formData.steps.map((step, index) => {
                const roleInfo = getRoleInfo(step.approver_role);
                return (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 border rounded-lg bg-muted/50"
                  >
                    <div className="flex items-center gap-3">
                      <Badge variant="outline">{step.step_order}</Badge>
                      <div>
                        <div className="font-medium">{step.step_name}</div>
                        <div className="text-sm text-muted-foreground">
                          {roleInfo.icon} {t(roleInfo.labelKey)}
                          {step.is_mandatory && (
                            <Badge variant="destructive" className="ml-2 text-xs">{t('approvalComp.mandatory')}</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => handleMoveStep(index, 'up')} disabled={index === 0}>
                        <ArrowUp className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleMoveStep(index, 'down')} disabled={index === formData.steps.length - 1}>
                        <ArrowDown className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteStep(index)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Step add/edit form */}
          <div className="border rounded-lg p-4 space-y-4">
            <h4 className="font-medium">
              {editingStepIndex !== null ? t('approvalComp.editStep') : t('approvalComp.addStep')}
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="step_name">{t('approvalComp.stepNameLabel')}</Label>
                <Input
                  id="step_name"
                  value={stepFormData.step_name}
                  onChange={(e) => handleStepInputChange('step_name', e.target.value)}
                  placeholder={t('approvalComp.stepNamePlaceholder')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="approver_role">{t('approvalComp.approverRoleLabel')}</Label>
                <Select
                  value={stepFormData.approver_role}
                  onValueChange={(value) => handleStepInputChange('approver_role', value)}
                >
                  <SelectTrigger id="approver_role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {APPROVER_ROLES.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.icon} {t(role.labelKey)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 space-y-2">
                <Label htmlFor="step_description">{t('approvalComp.stepDescLabel')}</Label>
                <Textarea
                  id="step_description"
                  value={stepFormData.description}
                  onChange={(e) => handleStepInputChange('description', e.target.value)}
                  placeholder={t('approvalComp.stepDescPlaceholder')}
                  rows={2}
                />
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is_mandatory"
                  checked={stepFormData.is_mandatory}
                  onCheckedChange={(checked) => handleStepInputChange('is_mandatory', checked)}
                />
                <Label htmlFor="is_mandatory">{t('approvalComp.stepMandatory')}</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="requires_comment"
                  checked={stepFormData.requires_comment}
                  onCheckedChange={(checked) => handleStepInputChange('requires_comment', checked)}
                />
                <Label htmlFor="requires_comment">{t('approvalComp.commentMandatory')}</Label>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              {editingStepIndex !== null && (
                <Button variant="outline" onClick={handleCancelStepEdit}>
                  <X className="mr-2 h-4 w-4" />
                  {t('common.cancel')}
                </Button>
              )}
              <Button
                onClick={editingStepIndex !== null ? handleUpdateStep : handleAddStep}
                disabled={!stepFormData.step_name}
              >
                <Plus className="mr-2 h-4 w-4" />
                {editingStepIndex !== null ? t('approvalComp.updateStep') : t('approvalComp.addStepBtn')}
              </Button>
            </div>
          </div>

          {formData.steps.length === 0 && (
            <div className="text-center py-6 text-muted-foreground border-2 border-dashed rounded-lg">
              <AlertCircle className="mx-auto h-8 w-8 mb-2" />
              <p>{t('approvalComp.noSteps')}</p>
              <p className="text-sm">{t('approvalComp.noStepsHint')}</p>
            </div>
          )}
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={handleCloseDialog}>{t('common.cancel')}</Button>
        <Button onClick={handleSubmit} disabled={formData.steps.length === 0}>
          {editingWorkflow ? t('approvalComp.update') : t('common.create')}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
  );
};

export default WorkflowFormDialog;
